import { z } from "zod";

// Phase 6 typed configSchema for the `option-set` template (D-01).
// Replaces the Phase 01.1 z.unknown() placeholder in TEMPLATE_CATALOG so that
// validateRecipe Pass 2A now enforces the per-widget config shape.
//
// PITFALLS §C1 (recipe-specific overfitting): NO per-option `metadata` escape
// hatch is permitted on this template. Rich per-option data (recipe-defined
// attributes such as ingredient profiles) flows through recipe-level
// OptionCategory[] declared at the recipe level per Phase 01.1 D-16..D-19.
// Adding a `metadata` field here would re-introduce the closed-enum overfit the
// project pivoted away from.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15): export the interface explicitly + annotate the const
// with z.ZodType<OptionSetConfig> so tsc can emit declarations without reading
// across module boundaries (no TS9013).
//
// Phase 7 D-07 addition: optional `tierId` field on each option (kebab-case id
// referencing a RankedTier.id when the option-set is paired with a ranked-tier
// widget via composition). Recipe-side cross-reference validation of
// tierId-to-RankedTier-existence is recipe-root walk territory and lives
// outside this template-level schema.
//
// Phase 11.7 addition: optional `groupId` field on each option (kebab-case id
// referencing a recipe-level OptionCategory.id). Render-time grouping/bucketing
// metadata (Surface 1). Identical shape + disposition to tierId — the
// groupId-to-OptionCategory-existence cross-check is validateRecipe territory,
// NOT this template-level schema (same as tierId/categoryIds).

export interface OptionSetConfig {
  options: ReadonlyArray<{
    id: string;
    label: string;
    categoryIds?: ReadonlyArray<string> | undefined;
    tierId?: string | undefined;
    groupId?: string | undefined;
  }>;
  cardinality: {
    min: number;
    max?: number | undefined;
  };
}

// The cardinality strictObject carries a superRefine that emits a custom issue
// when `max < min`. The refine attaches at the cardinality level so the issue
// path is `["cardinality"]` (more precise than attaching at the root).
const _cardinalitySchema = z
  .strictObject({
    min: z.number().int().nonnegative(),
    max: z.number().int().positive().optional(),
  })
  .superRefine((cardinality, ctx) => {
    if (cardinality.max !== undefined && cardinality.max < cardinality.min) {
      ctx.addIssue({
        code: "custom",
        message: "cardinality.max must be >= cardinality.min",
      });
    }
  });

const _optionEntrySchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  // Recipe-level OptionCategoryBlock ids (Phase 01.1 D-16..D-19) — referenced by id.
  // Each ref is a non-empty string; recipe-level cross-checks happen in validateRecipe.
  categoryIds: z.array(z.string().min(1)).optional(),
  // Phase 7 D-07: optional ranked-tier assignment. References RankedTier.id when
  // the option-set is paired with a ranked-tier widget via composition. The
  // tierId-to-RankedTier-existence cross-check lives at recipe-root walk time;
  // template-level schema only enforces the kebab-case id shape.
  tierId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required")
    .optional(),
  // Phase 11.7: optional render-time grouping id. References a recipe-level
  // OptionCategory.id when the option-set buckets its options into groups
  // (Surface 1). The groupId-to-OptionCategory-existence cross-check lives at
  // recipe-root walk time; template-level schema only enforces the kebab-case
  // id shape (mirrors tierId exactly).
  groupId: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required")
    .optional(),
});

// UI-SPEC empty-config fallback: recipes may legitimately ship `options: []`
// at author time (pre-populated forms still need to compile). Pass 2A allows
// `.min(0)` here; per-widget cardinality enforces selection rules at render time.
export const OptionSetConfigSchema: z.ZodType<OptionSetConfig> = z.strictObject({
  options: z.array(_optionEntrySchema).min(0),
  cardinality: _cardinalitySchema,
});
